import React, { useEffect, useState, useRef } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
interface MultiSelectProps {
  label: string;
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  className?: string;
}
export function MultiSelect({
  label,
  options,
  values,
  onChange,
  className
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);
  const toggle = (option: string) => {
    if (values.includes(option)) {
      onChange(values.filter(v => v !== option));
    } else {
      onChange([...values, option]);
    }
  };
  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };
  const count = values.length;
  const triggerLabel = count === 0 ? `All ${label}` : count === 1 ? values[0] : `${count} ${label} selected`;
  return <div ref={containerRef} className={cn('relative', className)} data-id="element-320">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={cn('h-9 px-3 py-1 text-sm bg-slate-50 border rounded-lg flex items-center gap-2 transition-colors min-w-[160px] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500', count > 0 ? 'border-indigo-300 bg-indigo-50/50 text-indigo-900' : 'border-slate-200 text-slate-700 hover:border-slate-300')} data-id="element-321">
        <span className="flex-1 text-left truncate" data-id="element-322">{triggerLabel}</span>
        {count > 0 ? <span role="button" tabIndex={0} onClick={clear} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange([]);
        }
      }} className="text-indigo-500 hover:text-indigo-700 p-0.5 rounded hover:bg-indigo-100" aria-label={`Clear ${label} filter`} data-id="element-323">
            <X className="w-3.5 h-3.5" data-id="element-324" />
          </span> : <ChevronDown className={cn('w-4 h-4 text-slate-400 transition-transform', open && 'rotate-180')} data-id="element-325" />}
      </button>

      {open && <div className="absolute z-30 mt-1 left-0 min-w-full w-max max-w-[280px] bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150" data-id="element-326">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between" data-id="element-327">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-328">
              {label}
            </span>
            {count > 0 && <button type="button" onClick={() => onChange([])} className="text-xs font-medium text-indigo-600 hover:text-indigo-800" data-id="element-329">
                Clear
              </button>}
          </div>
          <ul role="listbox" aria-multiselectable="true" className="max-h-64 overflow-y-auto py-1" data-id="element-330">
            {options.map(option => {
          const selected = values.includes(option);
          return <li key={option} data-id="element-331">
                  <button type="button" role="option" aria-selected={selected} onClick={() => toggle(option)} className={cn('w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors', selected ? 'text-slate-900 font-medium' : 'text-slate-700')} data-id="element-332">
                    <span className={cn('w-4 h-4 rounded border flex items-center justify-center flex-shrink-0', selected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 bg-white')} data-id="element-333">
                      {selected && <Check className="w-3 h-3" strokeWidth={3} data-id="element-334" />}
                    </span>
                    <span className="truncate" data-id="element-335">{option}</span>
                  </button>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
}